import { useState, useEffect, useRef, useCallback } from "react";
import { JobCardForm } from "@/pages/jobcards/types";
import { clientsApi } from "@/services/api";

interface ClientRow {
  id: number;
  name: string;
  company: string;
  email: string;
  phone: string;
  address: string;
  designation: string;
  gstin: string;
  status: string;
}

interface Props {
  form: JobCardForm;
  onChange: (patch: Partial<JobCardForm>) => void;
}

export default function SectionClientDetails({ form, onChange }: Props) {
  const [search, setSearch] = useState(form.clientName || "");
  const [showDropdown, setShowDropdown] = useState(false);
  const [clients, setClients] = useState<ClientRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Fetch clients from API with debounce
  const fetchClients = useCallback(async (searchTerm: string) => {
    setLoading(true);
    setError("");
    try {
      const res = await clientsApi.getAll({
        search: searchTerm || undefined,
        status: "active",
        limit: 50,
      }) as { data: ClientRow[] };
      setClients(res.data || []);
    } catch {
      setError("Could not load clients");
      setClients([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load initial clients list when dropdown opens
  const handleFocus = () => {
    setShowDropdown(true);
    if (clients.length === 0) {
      fetchClients(search);
    }
  };

  // Debounced search
  const handleSearchChange = (val: string) => {
    setSearch(val);
    setShowDropdown(true);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      fetchClients(val);
    }, 300);
  };

  const selectClient = (c: ClientRow) => {
    onChange({
      clientId:          String(c.id),
      clientName:        c.name,
      clientCompany:     c.company || "",
      clientEmail:       c.email || "",
      clientPhone:       c.phone || "",
      clientAddress:     c.address || "",
      clientDesignation: c.designation || "",
      clientGstin:       c.gstin || "",
    });
    setSearch(c.name);
    setShowDropdown(false);
  };

  const clearClient = () => {
    onChange({
      clientId:          "",
      clientName:        "",
      clientCompany:     "",
      clientEmail:       "",
      clientPhone:       "",
      clientAddress:     "",
      clientDesignation: "",
      clientGstin:       "",
    });
    setSearch("");
    setClients([]);
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
      <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
        <span className="w-5 h-5 flex items-center justify-center">
          <i className="ri-contacts-book-2-line text-amber-600 text-base" />
        </span>
        <h3 className="text-sm font-semibold text-gray-800">Section 4 — Client Details</h3>
        <span className="ml-auto text-xs text-gray-400 flex items-center gap-1">
          <i className="ri-database-2-line text-xs" />Live from DB
        </span>
      </div>
      <div className="p-5 space-y-4">
        {/* Client Search */}
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1.5">
            Search &amp; Select Client <span className="text-red-400">*</span>
          </label>
          <div className="relative" ref={dropdownRef}>
            <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none z-10">
              {loading
                ? <i className="ri-loader-4-line animate-spin text-gray-400 text-sm" />
                : <i className="ri-search-line text-gray-400 text-sm" />
              }
            </span>
            <input
              type="text"
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              onFocus={handleFocus}
              placeholder="Type client name or company..."
              className="w-full pl-9 pr-10 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 transition-all"
            />
            {form.clientId && (
              <button
                type="button"
                onClick={clearClient}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400 hover:text-red-500 cursor-pointer z-10"
              >
                <i className="ri-close-line text-sm" />
              </button>
            )}

            {showDropdown && (
              <div
                className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl z-20 overflow-hidden max-h-60 overflow-y-auto"
                style={{ boxShadow: "0 8px 24px rgba(0,0,0,0.1)" }}
              >
                {loading && (
                  <div className="px-4 py-3 text-sm text-gray-400 flex items-center gap-2">
                    <i className="ri-loader-4-line animate-spin text-sm" />
                    Loading clients...
                  </div>
                )}
                {error && !loading && (
                  <div className="px-4 py-3 text-sm text-red-500 flex items-center gap-2">
                    <i className="ri-error-warning-line text-sm" />
                    {error}
                    <button
                      type="button"
                      onClick={() => fetchClients(search)}
                      className="ml-auto text-xs text-amber-600 hover:underline cursor-pointer"
                    >
                      Retry
                    </button>
                  </div>
                )}
                {!loading && !error && clients.length === 0 && (
                  <div className="px-4 py-3 text-sm text-gray-400">
                    No clients found
                    {search && <span> matching &quot;{search}&quot;</span>}
                  </div>
                )}
                {!loading && !error && clients.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => selectClient(c)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-amber-50 cursor-pointer text-left transition-colors border-b border-gray-50 last:border-0"
                  >
                    <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 text-xs font-bold flex-shrink-0">
                      {c.name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-800 truncate">{c.name}</p>
                      <p className="text-xs text-gray-500 truncate">{c.company || "—"}</p>
                    </div>
                    {c.status !== "active" && (
                      <span className="text-xs text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded font-medium whitespace-nowrap">
                        {c.status}
                      </span>
                    )}
                  </button>
                ))}
                <div className="border-t border-gray-100 px-4 py-2">
                  <a
                    href="/clients"
                    className="text-xs text-amber-600 hover:underline cursor-pointer flex items-center gap-1"
                  >
                    <span className="w-3 h-3 flex items-center justify-center"><i className="ri-external-link-line text-xs" /></span>
                    Manage Clients
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Auto-filled fields — always shown once client is selected */}
        {form.clientId && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
            {[
              { label: "Client Name",  key: "clientName",        icon: "ri-user-line" },
              { label: "Company Name", key: "clientCompany",     icon: "ri-building-line" },
              { label: "Email",        key: "clientEmail",       icon: "ri-mail-line" },
              { label: "Phone",        key: "clientPhone",       icon: "ri-phone-line" },
              { label: "Designation",  key: "clientDesignation", icon: "ri-briefcase-line" },
              { label: "GSTIN",        key: "clientGstin",       icon: "ri-government-line" },
            ].map((f) => (
              <div key={f.key}>
                <label className="block text-xs font-medium text-gray-500 mb-1">{f.label}</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
                    <i className={`${f.icon} text-gray-300 text-sm`} />
                  </span>
                  <input
                    type="text"
                    value={(form as Record<string, string>)[f.key] || ""}
                    onChange={(e) => onChange({ [f.key]: e.target.value })}
                    className="w-full pl-9 pr-3 py-2 border border-gray-100 rounded-lg text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-300 focus:bg-white transition-all"
                  />
                </div>
              </div>
            ))}
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-gray-500 mb-1">Address</label>
              <textarea
                value={form.clientAddress}
                onChange={(e) => onChange({ clientAddress: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-gray-100 rounded-lg text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-300 focus:bg-white resize-none transition-all"
              />
            </div>

            {/* Contact Person — always visible */}
            <div className="sm:col-span-2 border-t border-gray-100 pt-4">
              <p className="text-xs font-semibold text-gray-600 mb-3 flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-contacts-line text-amber-500 text-sm" />
                </span>
                Contact Person at Site
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Contact Person Name</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
                      <i className="ri-user-star-line text-gray-300 text-sm" />
                    </span>
                    <input
                      type="text"
                      value={form.contactPerson}
                      onChange={(e) => onChange({ contactPerson: e.target.value })}
                      placeholder="e.g. Rajesh Verma (Site Engineer)"
                      className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-300 focus:bg-white transition-all"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Contact Person Phone</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
                      <i className="ri-phone-line text-gray-300 text-sm" />
                    </span>
                    <input
                      type="text"
                      value={form.contactPersonPhone}
                      onChange={(e) => onChange({ contactPersonPhone: e.target.value })}
                      placeholder="+91 98001 XXXXX"
                      className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-300 focus:bg-white transition-all"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {!form.clientId && (
          <div className="flex items-center gap-2 p-3 rounded-xl bg-amber-50 border border-amber-100">
            <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
              <i className="ri-information-line text-amber-500 text-sm" />
            </span>
            <p className="text-xs text-amber-700">Search and select a client above to auto-fill all details</p>
          </div>
        )}
      </div>
    </div>
  );
}
